import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Grid, GridColumn, List, ListItem, Notice, Separator } from 'telia-front-react';

import EmployeeUsagesStore from '../../../stores/EmployeeUsagesStore';
import InvoiceDetailsStore from '../../../stores/InvoiceDetailsStore';
import InvoiceFileStore from '../../../stores/InvoiceFileStore';
import InvoiceNameStore from '../../../stores/InvoiceNameStore';
import { ISsoStore } from '../../../stores/SsoStore';
import { ITranslateStore } from '../../../stores/TranslateStore';
import { formatDate } from '../../../util/DateUtil';
import NoticeWithButton from '../../common/NoticeWithButton';

interface IInvoiceDetailsHeaderProps {
  invoiceDetailsStore?: InvoiceDetailsStore;
  invoiceNameStore?: InvoiceNameStore;
  ssoStore?: ISsoStore;
  translateStore?: ITranslateStore;
  invoiceFileStore?: InvoiceFileStore;
  employeeUsagesStore?: EmployeeUsagesStore;
}

@inject('invoiceDetailsStore', 'invoiceNameStore', 'ssoStore', 'translateStore', 'invoiceFileStore', 'employeeUsagesStore')
@observer
export default class InvoiceDetailsHeader extends React.Component<IInvoiceDetailsHeaderProps> {
  public render(): JSX.Element {
    const invoiceDetailsStore = this.props.invoiceDetailsStore;
    const invoiceNameStore = this.props.invoiceNameStore;
    const invoiceDetailsRequest = invoiceDetailsStore.invoiceDetailsRequest;
    const translate = this.props.translateStore.translate;

    return (
      <React.Fragment>
        <Grid>
          <GridColumn width={['xs-12', 'sm-12', 'md-7', 'lg-7']}>
            <h1 className="h4">{invoiceNameStore.defaultInvoiceName(invoiceDetailsRequest.contentType)}</h1>
            <p className="h3">{this.props.ssoStore.customer.displayName}</p>
          </GridColumn>
          <GridColumn width={['sm-12', 'md-5', 'lg-5']}>
            <div className="card__content--padding-default">
              <List type="unstyled" fullWidth={true}>
                <ListItem>
                  <Grid>
                    <GridColumn width={['sm-7', 'xs-6']}>{translate('shared.invoice-details-modal.invoice-number')}</GridColumn>
                    <GridColumn id="invoice-details-header-invoice-number" width={['sm-5', 'xs']} className="text-left">
                      {invoiceDetailsRequest.invoiceNumber}
                    </GridColumn>
                  </Grid>
                </ListItem>
                <ListItem>
                  <Grid>
                    <GridColumn width={['sm-7', 'xs-6']}>{translate('shared.invoice-details-modal.reference-number')}</GridColumn>
                    <GridColumn id="invoice-details-header-reference-number" width={['sm-5', 'xs']} className="text-left">
                      {invoiceDetailsRequest.referenceNumber}
                    </GridColumn>
                  </Grid>
                </ListItem>
                <ListItem>
                  <Grid>
                    <GridColumn width={['sm-7', 'xs-6']}>{translate('shared.invoice-details-modal.invoice-date')}</GridColumn>
                    <GridColumn id="invoice-details-header-invoice-date" width={['sm-5', 'xs']} className="text-left">
                      {formatDate(invoiceDetailsRequest.issuedDate)}
                    </GridColumn>
                  </Grid>
                </ListItem>
                <ListItem className="text-bold">
                  <Grid>
                    <GridColumn width={['sm-7', 'xs-6']}>{translate('shared.invoice-details-modal.payment-due')}</GridColumn>
                    <GridColumn id="invoice-details-header-due-date" width={['sm-5', 'xs']} className="text-left">
                      {formatDate(invoiceDetailsRequest.dueDate)}
                    </GridColumn>
                  </Grid>
                </ListItem>
              </List>
            </div>
          </GridColumn>
        </Grid>
        {this.restrictedToEmployeeNotice()}
        {this.noUsageMandateNotice()}
        <Separator spacing="xxsmall" color="invisible" />
      </React.Fragment>
    );
  }

  private noUsageMandateNotice() {
    const notAllowedToShowUsagesButCanBeAllowed = this.props.invoiceDetailsStore.notAllowedToShowUsagesButCanBeAllowed;
    const translate = this.props.translateStore.translate;

    if (notAllowedToShowUsagesButCanBeAllowed) {
      return (
        <React.Fragment>
          <Separator color="invisible" spacing="xxsmall" />
          <Notice>
            <span id="invoice-details-no-usage-mandate-notice">{translate('shared.invoice-details-modal.notices.no-usage-mandate')}</span>
          </Notice>
        </React.Fragment>
      );
    }
  }

  private restrictedToEmployeeNotice() {
    const translate = this.props.translateStore.translate;
    const invoiceFileStore = this.props.invoiceFileStore;
    const invoiceNumber = this.props.invoiceDetailsStore.currentInvoiceNumber;
    const source = this.props.invoiceDetailsStore.invoiceDetailsRequest.source;

    if (this.props.employeeUsagesStore.showEmployeeInvoiceUsagesHeaderNotice) {
      return (
        <React.Fragment>
          <Separator spacing="xsmall" color="invisible" />
          <NoticeWithButton
            gaEvent="Link - usages for employee"
            noticeType="employee"
            noticeText={translate('shared.invoice-details-modal.employee-usages-check.notice')}
            buttonProps={{
              id: 'invoice-details-modal-invoice-line-usages-employee-button',
              iconRight: '#arrow-right',
              text: translate('shared.invoice-details-modal.employee-usages-check.button'),
              type: 'employee',
              onClick: () => invoiceFileStore.approveAndDownloadUsages(invoiceNumber, source)
            }}
          />
          <Separator spacing="xxsmall" color="invisible" />
        </React.Fragment>
      );
    }

    return null;
  }
}
